import React from "react";
import { Link } from "react-router-dom";
import PublicShell, { LegalSection } from "@/components/PublicShell";

const ENTITY = "Zealous FZE LLC";
const JURISDICTION = "the United Arab Emirates";

export default function Privacy() {
  return (
    <PublicShell
      eyebrow="Legal"
      title="Privacy Policy"
      intro="What we collect, why we process it, who it is shared with, and how to have it deleted."
      updated="June 2026"
    >
      <LegalSection n={1} title="Who controls your data">
        <p>
          Matiqs is operated by {ENTITY}, registered in {JURISDICTION}, acting as the controller of the personal data
          described here. For any privacy matter, contact{" "}
          <a href="mailto:admin@matiqs.net" className="text-brand font-semibold hover:underline">admin@matiqs.net</a>.
        </p>
      </LegalSection>

      <LegalSection n={2} title="What we collect">
        <p><strong>Account details</strong> — your email address, a securely hashed password, and the date you registered.</p>
        <p>
          <strong>Company profile</strong> — company name, logo, vendor type, trades, address, city, country, phone,
          contact email, website, year established, description, trade licence and VAT/TRN numbers, and the projects you
          choose to publish.
        </p>
        <p>
          <strong>Business files you upload</strong> — your price list (catalogue), contractor Bills of Quantities, the
          quotations and revisions you generate, and compliance documents such as trade licences, VAT certificates,
          insurance policies and other certificates.
        </p>
        <p>
          <strong>E-invoicing data</strong> — for the e-invoicing service: your Contract/PO reference and high-level Contract BOQ, each
          Interim Payment Certificate's progress (cumulative % per line) and any advance/retention items,
          and the <strong>buyer details you enter</strong> (company name, address, tax registration number / TRN and Peppol ID).
          This is used to compute and generate your tax invoices and is private to your account.
        </p>
        <p>
          <strong>Technical data</strong> — server logs containing IP address, timestamps, requested endpoints and error
          traces, used to keep the service secure and working.
        </p>
        <p>We do not ask for payment card numbers directly; where paid plans are enabled, card data is handled by the payment provider, not by us.</p>
      </LegalSection>

      <LegalSection n={3} title="Why we process it">
        <p>
          To provide the service you asked for: hosting your catalogue, matching BOQ lines, reading document expiry
          dates, generating PDF and Excel outputs, reconciling and generating your UAE FTA e-invoicing documents, showing
          your public listing, and letting contractors contact you.
        </p>
        <p>
          To keep the platform safe and trustworthy: authentication, abuse prevention, verifying that uploaded documents
          are current, and enforcing plan limits.
        </p>
        <p>
          To support and improve the product: responding to your emails, diagnosing faults, and understanding aggregate
          usage. Where the law requires consent — for example marketing email — we will ask for it and you can withdraw
          it at any time.
        </p>
      </LegalSection>

      <LegalSection n={4} title="AI processing — stated plainly">
        <p>
          Matiqs uses third-party AI providers to do two things:
        </p>
        <p>
          <strong>(a) BOQ matching.</strong> The line descriptions in an uploaded BOQ, together with your catalogue item
          descriptions, units and rates, are sent to <strong>Anthropic (Claude)</strong> so each line can be matched to
          the right catalogue item.
        </p>
        <p>
          <strong>(b) Document expiry reading.</strong> Compliance documents you upload are sent to{" "}
          <strong>Google (Gemini)</strong> to extract the expiry date, issuing authority and document number.
        </p>
        <p>
          These providers process the content to return a result to us. <strong>Your data is not used to train their
          models,</strong> and we do not sell or share your files with any other vendor, contractor or third party. If
          you would rather not have a document read by AI, do not upload it — you can enter expiry dates manually.
        </p>
        <p>
          <strong>E-invoicing &amp; ASP submission:</strong> your e-invoice data is <strong>not</strong> transmitted to any
          Accredited Service Provider or to the FTA today — Matiqs Qonnect only generates documents for you to download.
          Matiqs Qonnect is a lightweight ERP that partners with Approved Service Providers; if a connected submission option is launched in
          future, sending your invoice data to the ASP will be your explicit choice at the point of submission.
        </p>
      </LegalSection>

      <LegalSection n={5} title="What is public and what is not">
        <p>
          <strong>Never public:</strong> your catalogue and rates, your uploaded BOQ files, your quotations and
          revisions, and the contents of your compliance documents.
        </p>
        <p>
          <strong>Public when you are listed:</strong> company name, logo, vendor type, trades, city and country,
          description, website, year established, published projects, contact email, and the status of your documents
          (Valid / Expiring soon / Expired) — never the document files or their contents. You can turn public listing off
          in your Vendor Profile at any time.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Where your data is stored">
        <p>
          Data is held in our managed database and object storage on cloud infrastructure operated by our hosting
          providers. Processing may take place outside your country, including where our AI providers operate. We rely on
          appropriate safeguards for such transfers, and we require providers to keep the data confidential and secure.
        </p>
      </LegalSection>

      <LegalSection n={7} title="How long we keep it">
        <p>
          Account, profile, catalogue, quotation and document data is kept while your account is active. If you close
          your account, we delete or de-identify it within 90 days, except where we must keep records longer for legal,
          tax or dispute-resolution reasons. Server logs are kept for a short operational period. Backups may retain
          copies briefly after deletion before they cycle out.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Security">
        <p>
          Passwords are stored only as salted hashes. Access to the service requires an authenticated token, and quote,
          catalogue and document endpoints are scoped to your account — one vendor cannot read another vendor's data.
          Uploaded files are stored in access-controlled object storage. No system is perfectly secure, so please use a
          strong, unique password and tell us immediately if you suspect unauthorised access.
        </p>
      </LegalSection>

      <LegalSection n={9} title="Your rights">
        <p>
          You can ask us to access, correct, export or delete your data, to restrict or object to certain processing, or
          to withdraw consent where we relied on it. Most of this you can do yourself: edit your profile, export your
          catalogue to Excel, delete catalogue items, quotations or documents at any time.
        </p>
        <p>
          For anything else, email{" "}
          <a href="mailto:admin@matiqs.net" className="text-brand font-semibold hover:underline">admin@matiqs.net</a>{" "}
          from your registered address and we will respond within 30 days. You may also complain to the data protection
          authority in {JURISDICTION}.
        </p>
      </LegalSection>

      <LegalSection n={10} title="Cookies and similar technologies">
        <p>
          We use only what the service needs: a token stored in your browser to keep you signed in, and basic
          preferences. We do not run third-party advertising or cross-site tracking cookies. Clearing your browser
          storage will sign you out.
        </p>
      </LegalSection>

      <LegalSection n={11} title="Children">
        <p>Matiqs is a business tool and is not intended for anyone under 18. We do not knowingly collect data from children.</p>
      </LegalSection>

      <LegalSection n={12} title="Changes to this policy">
        <p>
          We may update this policy as the product develops. Material changes will be notified in the service or by
          email, and the “last updated” date above will change. Related reading:{" "}
          <Link to="/terms" className="text-primary font-semibold hover:underline">Terms of Service</Link>.
        </p>
      </LegalSection>
    </PublicShell>
  );
}
